/**
 * Whose account is it? (S-27)
 *
 * A client registers the account they will send from, and the name on it is typed by them. The
 * first transfer from each new account is looked at: the bank's sender name against the KYC legal
 * name. An exact match verifies the account without anyone; everything else lands here, with both
 * names side by side. The manager verifies the account (the names are the same person's, or the
 * client's transfer receipt shows it) or refuses it (the deposit is not credited and the money goes
 * back to the account it came from).
 */
import { useCallback, useEffect, useState } from 'react';
import PageHeader from '../../components/layout/PageHeader';
import StatusBadge from '../../components/StatusBadge';
import sourceAccountsService, {
  type ReviewDecision,
  type SourceAccountReview,
} from '../../services/sourceAccounts.service';
import { cleanMfaInput, stepUpErrorMessage } from '../../lib/mfaApproval';
import { formatDateTime } from '../../lib/datetime';
import { colors } from '../../styles/theme';
import { canDecide, noteHint, oldestFirst, reasonLabel } from './sourceAccounts.helpers';
import { formatCents } from '../../lib/amount';

function NameLine({ label, name }: Readonly<{ label: string; name: string | null }>): JSX.Element {
  return (
    <div>
      <p className="text-xs" style={{ color: colors.muted }}>{label}</p>
      <p className="text-sm font-semibold" style={{ color: colors.ink }} translate="no">
        {name ?? '—'}
      </p>
    </div>
  );
}

function ReviewCard({
  review,
  onDone,
}: Readonly<{ review: SourceAccountReview; onDone: (message: string) => void }>): JSX.Element {
  const [note, setNote] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const decide = async (decision: ReviewDecision): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await sourceAccountsService.decide(
        review.bank_transaction_id,
        decision,
        note.trim(),
        code.trim(),
      );
      onDone(
        decision === 'verify'
          ? 'Account verified.'
          : 'Account refused; the deposit was rejected.',
      );
    } catch (e: unknown) {
      setError(stepUpErrorMessage(e, 'Could not record the decision.'));
    } finally {
      setBusy(false);
    }
  };

  const ready = canDecide(review, note, code);

  return (
    <li
      className="rounded-2xl p-5"
      style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold" style={{ color: colors.ink }}>
            {review.currency_code} {formatCents(review.amount)} · account ····
            {review.account_last4}
          </p>
          <p className="text-xs" style={{ color: colors.muted }}>
            Received {formatDateTime(review.posted_at)}
          </p>
        </div>
        <StatusBadge tone="warning">{reasonLabel(review.reason)}</StatusBadge>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
        <NameLine label="Name the bank reported" name={review.reported_sender_name} />
        <NameLine label="Name on the client’s KYC file" name={review.kyc_legal_name} />
      </div>

      <label
        className="block text-xs mt-4 mb-1"
        htmlFor={`note-${review.bank_transaction_id}`}
        style={{ color: colors.muted }}
      >
        {noteHint(review.reason)}
      </label>
      <textarea
        id={`note-${review.bank_transaction_id}`}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={2}
        maxLength={1000}
        className="w-full px-3 py-2 rounded-lg text-sm"
        style={{ border: `1px solid ${colors.border}` }}
      />

      <div className="flex flex-wrap items-end gap-3 mt-3">
        <div>
          <label
            className="block text-xs mb-1"
            htmlFor={`code-${review.bank_transaction_id}`}
            style={{ color: colors.muted }}
          >
            Authentication code
          </label>
          <input
            id={`code-${review.bank_transaction_id}`}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(cleanMfaInput(e.target.value))}
            className="px-3 py-2 rounded-lg text-sm font-mono tracking-widest w-36"
            style={{ border: `1px solid ${colors.border}` }}
          />
        </div>
        <button
          type="button"
          disabled={busy || !ready}
          onClick={() => { void decide('verify'); }}
          className="text-sm px-3 py-2 rounded-lg font-semibold"
          style={{ background: colors.gold, color: colors.navy }}
        >
          Verify the account
        </button>
        <button
          type="button"
          disabled={busy || !ready}
          onClick={() => { void decide('refuse'); }}
          className="text-sm px-3 py-2 rounded-lg font-semibold"
          style={{
            background: 'transparent',
            color: colors.danger,
            border: `1px solid ${colors.dangerBorder}`,
          }}
        >
          Refuse it
        </button>
      </div>
      {error && (
        <p className="text-sm mt-3" role="alert" style={{ color: colors.danger }}>
          {error}
        </p>
      )}
    </li>
  );
}

export default function SourceAccounts(): JSX.Element {
  const [rows, setRows] = useState<SourceAccountReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(oldestFirst(await sourceAccountsService.listReviews()));
    } catch {
      setError('Could not load the accounts waiting to be confirmed.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void reload(); }, [reload]);

  return (
    <div className="max-w-4xl">
      <PageHeader
        title="Source accounts"
        subtitle="Transfers waiting because the account they came from is not yet proved to be the client’s own."
      />
      {error && (
        <p className="mb-4 text-sm" role="alert" style={{ color: colors.danger }}>
          {error}
        </p>
      )}
      {flash && (
        <output
          className="block mb-4 p-3 rounded-lg text-sm"
          style={{ background: colors.successBg, color: colors.success }}
        >
          {flash}
        </output>
      )}
      {loading && <p className="text-sm" style={{ color: colors.muted }}>Loading…</p>}
      {!loading && rows.length === 0 && (
        <p className="text-sm" style={{ color: colors.muted }}>
          Nothing is waiting. Every transfer came from an account that matched its owner’s name or
          was confirmed.
        </p>
      )}
      <ul className="space-y-3">
        {rows.map((r) => (
          <ReviewCard
            key={r.bank_transaction_id}
            review={r}
            onDone={(message) => { setFlash(message); void reload(); }}
          />
        ))}
      </ul>
    </div>
  );
}
